import { useQueryCurrentLockerUtilisation } from '../dashboard.queries';
import { useCurrentStats } from './useCurrentStats';
import { useHistoricStats } from './useHistoricStats';
import { useMedianTimeInLocker } from './useMedianTimeInLocker';
import { useTimelineUpdatesEventCentre } from '../../../events/timelineUpdates/useTimelineUpdatesEventCentre.hook';
import { useMemo } from 'react';
import { useQueryGetProjectChildProjects } from '../../../core/queries/projects.queries';
import { QueryKeys } from '../../../core/queries/query-keys.constants';
import { useThrottledInvalidateQueries } from '../../../hooks/use-throttled-invalidate-queries.hook';
import {
  TimelineUpdatesSubscriptionMatcher,
  TimelineUpdatesSubscriptionStream
} from '../../../events/timelineUpdates/timelineUpdates.types';
import { STATS_REFRESH_INTERVAL_MS } from '../dashboard.constants';

export function useDashboardStats(projectID: string) {
  const childProjects = useQueryGetProjectChildProjects(projectID);
  const projectIDsInScope = useMemo(() => (childProjects.data || []).map((p) => p.id), [childProjects.data]);

  const currentStats = useCurrentStats(projectID);
  const historicStats = useHistoricStats(projectID);
  const medianTimeInLocker = useMedianTimeInLocker(projectID);
  const lockerUtilisation = useQueryCurrentLockerUtilisation(projectID, STATS_REFRESH_INTERVAL_MS);

  // subscribe to timeline update events. The stats queries are heavy, so invalidations are
  // buffered to at most one per interval regardless of event volume.
  const callback = useThrottledInvalidateQueries([QueryKeys.DASHBOARD], STATS_REFRESH_INTERVAL_MS);

  const matcher: TimelineUpdatesSubscriptionMatcher = useMemo(
    () => (event) => {
      return event.data.projectID && projectIDsInScope.includes(event.data.projectID) ? true : false;
    },
    [projectIDsInScope]
  );

  const stream: TimelineUpdatesSubscriptionStream = useMemo(
    () => ({
      deliveryIDs: [],
      workflowIDs: [],
      projectIDs: projectIDsInScope
    }),
    [projectIDsInScope]
  );

  useTimelineUpdatesEventCentre({ callback, matcher, stream });

  return { currentStats, lockerUtilisation, historicStats, medianTimeInLocker } as const;
}
